import { ChoiceCard, ChoiceCardGroup } from "@coval/ui"
import { cn } from "@coval/ui/cn"

const AGENT_SELECTOR_TITLE = "Uploaded conversations agent selector"
const LENS_SELECTOR_TITLE = "Uploaded conversations lens selector"
const CONTROLS_TITLE = "Uploaded conversations visualizer controls"

const LENS_CTA_COPY: Partial<Record<string, string>> = {
  anomalies: "Send calls to Human Review",
  "call-topic": "Create test sets",
  personas: "Create Coval Personas",
}

interface VisualizerLensOption {
  key: string
  subtitle: string
  title: string
}

interface VisualizerAgentOption {
  key: string
  label: string
}

interface UploadedConversationsVisualizerControlsColumnProps {
  agents: VisualizerAgentOption[]
  hasMultipleAgents: boolean
  onSelectAgent: (agentKey: string) => void
  onSelectLens: (lensKey: string) => void
  selectedAgentKey: string
  selectedLensKey: string | null
  selectedLenses: VisualizerLensOption[]
}

export function UploadedConversationsVisualizerControlsColumn({
  agents,
  hasMultipleAgents,
  onSelectAgent,
  onSelectLens,
  selectedAgentKey,
  selectedLensKey,
  selectedLenses,
}: UploadedConversationsVisualizerControlsColumnProps) {
  const selectedLensCtaCopy = selectedLensKey
    ? LENS_CTA_COPY[selectedLensKey]
    : undefined

  return (
    <div
      className="flex min-h-0 flex-col self-stretch @min-[580px]/visualizer:h-[320px] @min-[580px]/visualizer:overflow-y-auto"
      role="group"
      aria-label={CONTROLS_TITLE}
    >
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-2">
        {hasMultipleAgents ? (
          <div
            className="space-y-2"
            data-testid="uploaded-conversations-agent-selector"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-coval-text-muted">
              Agent
            </p>
            <ChoiceCardGroup
              aria-label={AGENT_SELECTOR_TITLE}
              className="grid grid-cols-2 gap-2"
            >
              {agents.map((agent) => (
                <ChoiceCard
                  key={agent.key}
                  title={agent.label}
                  selected={agent.key === selectedAgentKey}
                  onSelect={() => onSelectAgent(agent.key)}
                  className="min-h-0 px-3 py-2"
                  data-testid={`uploaded-conversations-agent-${agent.key}`}
                />
              ))}
            </ChoiceCardGroup>
          </div>
        ) : null}

        <div
          className="space-y-2"
          role="group"
          aria-label={LENS_SELECTOR_TITLE}
          data-testid="uploaded-conversations-lens-selector"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-coval-text-muted">
            Lens
          </p>
          <ul className="space-y-2">
            {selectedLenses.map((lens) => {
              const isSelected = lens.key === selectedLensKey
              return (
                <li key={lens.key}>
                  <ChoiceCard
                    title={lens.title}
                    description={lens.subtitle}
                    selected={isSelected}
                    onSelect={() => onSelectLens(lens.key)}
                    className="min-h-0 px-3 py-2"
                    data-testid={`uploaded-conversations-lens-${lens.key}`}
                  />
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      {selectedLensCtaCopy ? (
        <div
          className="space-y-1 pt-2"
          data-testid="uploaded-conversations-lens-cta"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-coval-text-muted">
            Next step
          </p>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between rounded-md border border-coval-border-active bg-surface-default px-3 py-2 text-left text-sm font-medium text-coval-text shadow-sm transition-colors",
              "focus-visible:outline-none",
              "hover:bg-surface-default-hover",
            )}
            onClick={() => {}}
            data-testid={`uploaded-conversations-lens-cta-${selectedLensKey}`}
          >
            <span>{selectedLensCtaCopy}</span>
            <span aria-hidden="true" className="text-coval-text-muted">
              &rarr;
            </span>
          </button>
        </div>
      ) : null}
    </div>
  )
}
